import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router";
import { appendScripts } from "@nxplatform/helpers/common/common";
import { getIsMobile } from "@nxplatform/helpers/helpers-pure";
import { allowedLinks } from "@nxplatform/platform-settings/locale-setup.routes";
import { loadCmsScripts } from "@nxplatform/ssr-service/services/load-cms-scripts";
import { setScriptsLoaded } from "@service/api-client/slices";
import { fetchSmartBanner } from "../../cms/banners.slice.js";
import { getQueryParam } from "../../hooks/query.hook.js";
const safeRequestIdleCallback = (callback) => {
  if ("requestIdleCallback" in window) {
    requestIdleCallback(callback);
  } else {
    setTimeout(callback, 1500);
  }
};
const bodyLoaderClass = "animation-start";
const bodyAppReadyClass = "appReady";
const mobileDeviceClass = "is-mobile-device";
const useAppSetup = ({
  loaded,
  setup
}) => {
  const isMobile = getIsMobile();
  const dispatch = useDispatch();
  const scriptBlock = !!getQueryParam().get("scriptBlock");
  const [appReady, setAppReady] = useState(false);
  const { pathname, search } = useLocation();
  useEffect(() => {
    setup();
  }, []);
  useEffect(() => {
    var _a, _b;
    if (loaded) {
      const isRegistrationPage = pathname === allowedLinks.registrationPage || pathname === allowedLinks.popanderRegistration;
      setAppReady(true);
      if (isRegistrationPage) return;
      document.body.classList.remove(bodyLoaderClass);
      (_a = document.getElementById("app-loader")) == null ? void 0 : _a.remove();
      (_b = document.getElementById("load_content")) == null ? void 0 : _b.remove();
      document.body.classList.add(bodyAppReadyClass);
    }
  }, [loaded, pathname]);
  const scriptsLoaded = (type, scripts) => dispatch(setScriptsLoaded({ [type]: scripts }));
  const loadScriptStart = async () => {
    await loadCmsScripts(`${pathname}${search}`, window.apiCmsHost);
    if (window.bodyScripts) {
      appendScripts(JSON.parse(window.bodyScripts), document.body, scriptsLoaded.bind(void 0, "body"));
    }
    if (window.headJavaScript) {
      appendScripts(JSON.parse(window.headJavaScript), document.body, scriptsLoaded.bind(void 0, "head"));
    }
    if (window.liveChatScript && !window.persistedBfCache) {
      appendScripts(JSON.parse(window.liveChatScript), document.body, scriptsLoaded.bind(void 0, "body"));
    }
  };
  useEffect(() => {
    if (appReady && !scriptBlock && !window.isPrerender && !window.googlePageSpeed) {
      if ("PerformanceObserver" in window && PerformanceObserver.supportedEntryTypes.includes("largest-contentful-paint")) {
        let lastLCPTime = 0;
        const observer = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          const lcpEntries = entries.filter((entry) => entry.entryType === "largest-contentful-paint");
          const lastLCP = lcpEntries[lcpEntries.length - 1];
          if (lastLCP && lastLCP.startTime > lastLCPTime) {
            lastLCPTime = lastLCP.startTime;
            setTimeout(() => {
              safeRequestIdleCallback(() => {
                requestAnimationFrame(() => loadScriptStart());
                if (window.isMobile) {
                  dispatch(fetchSmartBanner());
                }
              });
            }, 1500);
            observer.disconnect();
          }
        });
        observer.observe({ type: "largest-contentful-paint", buffered: true });
      } else {
        setTimeout(() => {
          safeRequestIdleCallback(() => {
            requestAnimationFrame(() => loadScriptStart());
            if (window.isMobile) {
              dispatch(fetchSmartBanner());
            }
          });
        }, 2500);
      }
    }
  }, [appReady, scriptBlock]);
  useEffect(() => {
    const { classList } = document.documentElement;
    if (isMobile) classList.add(mobileDeviceClass);
    else classList.remove(mobileDeviceClass);
    return () => classList.remove(mobileDeviceClass);
  }, [isMobile]);
  return {
    appReady
  };
};
export {
  bodyAppReadyClass,
  bodyLoaderClass,
  mobileDeviceClass,
  useAppSetup
};
